// Blueprint: crap/blueprint/call-task-lifecycle/architecture/001-booking-retention.md
import { REQUEST_LEAVE_EVENT } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { EventBus } from '@servicetitan/web-components';
import appManager from '@app/appManager';
import InboxModule from '@modules/InboxModule';
import { inboxState } from '@modules/InboxModule/state';
import type { DeferredSelection } from '@modules/TasksModule/types';
import TasksModule from '@modules/TasksModule';
import { requestLeave, type LeaveDecisionHandlers } from './leaveHandshake';

/**
 * Registry of mounted customer-mfe views and whether each has a booking in
 * progress, keyed by the task's stable `viewKey` (survives an id re-key).
 * Module-scoped and read synchronously on purpose: the mounted-view cap reads
 * it at selection change. Components that render from it subscribe with
 * `subscribeGuards`.
 *
 * A booking on the view the CSR is looking at is asked about by that view's
 * own customer-mfe prompt (the `request-leave` handshake); a booking on a
 * hidden view by the host's prompts, which name it.
 */

export type CloseDecision = {
  decision: 'proceed' | 'abort';
  /** A selection request that arrived while the prompt was open, for the caller to apply. */
  deferredSelection?: DeferredSelection;
};

type Registration = {
  token: number;
  bus: EventBus;
  isBookingGuarded: () => boolean;
  /** Name of the customer the view has resolved, if any. */
  getCustomerName?: () => string | undefined;
  onCloseConfirmed?: () => void;
};

type PendingRequest = { abort: () => void };

/** The host's Close discard prompt, while one is open. */
export type ClosePrompt = {
  viewKey: string;
  decide: (decision: CloseDecision['decision']) => void;
};

/**
 * Registry key for the Inbox home view's customer-mfe. Task view keys are
 * UUIDs, so the prefix cannot collide with one.
 */
export const HOME_VIEW_KEY = 'inbox-home:customer-search';

const logger = appManager.logger('Inbox');

const registrations = new Map<string, Registration>();
// Keyed by registration token: a pending request belongs to the registration
// that sent it, not to the viewKey entry (a remount can replace the entry).
// Close requests, the host prompt's and customer-mfe's alike.
const pendingByToken = new Map<number, PendingRequest>();
// Customer-mfe Close handshakes only, so a repeat Close can rejoin its modal.
const handshakeTokens = new Set<number>();
// Route-leave requests, kept apart from Close requests so they never read as a
// pending Close.
const pendingLeaveByToken = new Map<number, PendingRequest>();
let lastToken = 0;

const guardListeners = new Set<() => void>();

/** Subscribes to registry changes: a view registering, leaving or changing its booking state. */
export function subscribeGuards(listener: () => void): () => void {
  guardListeners.add(listener);
  return () => guardListeners.delete(listener);
}

/** Tells subscribers a registered view's booking state may have changed. */
export function notifyGuardsChanged(): void {
  guardListeners.forEach((listener) => listener());
}

let closePrompt: ClosePrompt | undefined;
const closePromptListeners = new Set<() => void>();

export function subscribeClosePrompt(listener: () => void): () => void {
  closePromptListeners.add(listener);
  return () => closePromptListeners.delete(listener);
}

export function getClosePrompt(): ClosePrompt | undefined {
  return closePrompt;
}

const setClosePrompt = (next: ClosePrompt | undefined) => {
  closePrompt = next;
  closePromptListeners.forEach((listener) => listener());
};

/** True while any Close is asking: the host prompt, or a customer-mfe Close handshake. */
export function isCloseAsking(): boolean {
  return !!closePrompt || pendingByToken.size > 0;
}

let leaveDialogOpen = false;

/** `InboxLeaveGuard` reports its host "Leave Inbox?" dialog opening and closing. */
export function setLeaveDialogOpen(open: boolean): void {
  leaveDialogOpen = open;
}

/** True while leaving the Inbox is asking: a customer-mfe leave handshake or the host dialog. */
export function isLeaveAsking(): boolean {
  return leaveDialogOpen || pendingLeaveByToken.size > 0;
}

export function registerTaskCloseGuard(
  viewKey: string,
  registration: {
    bus: EventBus;
    isBookingGuarded: () => boolean;
    getCustomerName?: () => string | undefined;
    onCloseConfirmed?: () => void;
  },
): number {
  lastToken += 1;
  registrations.set(viewKey, { token: lastToken, ...registration });
  notifyGuardsChanged();
  return lastToken;
}

/**
 * Removes the registration only if `token` still owns its entry, so a remount
 * that registered the new adapter first cannot lose its registration. Always
 * settles this token's pending requests as abort, even when a newer
 * registration already owns the entry.
 */
export function unregisterTaskCloseGuard(token: number): void {
  for (const [viewKey, registration] of registrations) {
    if (registration.token === token) {
      registrations.delete(viewKey);
    }
  }
  pendingByToken.get(token)?.abort();
  pendingLeaveByToken.get(token)?.abort();
  notifyGuardsChanged();
}

/** True only while a customer-mfe Close handshake waits for its `leave-decision`. */
export function isHandshakePending(viewKey: string): boolean {
  const registration = registrations.get(viewKey);
  return !!registration && handshakeTokens.has(registration.token);
}

/** Non-reactive: does the card's MFE report a booking in progress? */
export function isGuarded(viewKey: string): boolean {
  return registrations.get(viewKey)?.isBookingGuarded() ?? false;
}

/** Non-reactive: the registered views whose MFE reports a booking in progress. */
export function listGuardedViewKeys(): string[] {
  return [...registrations]
    .filter(([, registration]) => registration.isBookingGuarded())
    .map(([viewKey]) => viewKey);
}

/** Non-reactive: the customer a registered view has resolved, if any; a blank name counts as none. */
export function getCustomerName(viewKey: string): string | undefined {
  const name = registrations.get(viewKey)?.getCustomerName?.();
  return name === '' ? undefined : name;
}

/**
 * Sends an event to a registered view's customer-mfe, e.g. `SET_CUSTOMER_EVENT`
 * to the home view. Returns false when no view is registered under the key.
 */
export function emitToView(
  viewKey: string,
  event: string,
  payload?: Parameters<EventBus['emit']>[1],
): boolean {
  const registration = registrations.get(viewKey);
  if (!registration) {
    return false;
  }
  registration.bus.emit(event, payload);
  return true;
}

/**
 * Runs one `request-leave` handshake for a registration: tracks it in
 * `pending` so an unregister aborts it, settles once, and releases the
 * selection hold on every exit.
 *
 * @param registration - the view whose MFE is asked
 * @param pending - the pending-request map this handshake belongs to
 * @param holdToken - the selection hold to release
 * @param onSettled - called once with the decision and the deferred selection
 * @returns a dispose function that drops the handshake and releases the hold
 *   without settling
 */
function askToLeave(
  registration: Registration,
  pending: Map<number, PendingRequest>,
  holdToken: number,
  onSettled: (
    decision: CloseDecision['decision'],
    deferredSelection: DeferredSelection | undefined,
  ) => void,
): () => void {
  let settled = false;
  let dispose = () => {};
  const stop = () => {
    settled = true;
    pending.delete(registration.token);
    dispose();
  };
  const finish = (decision: CloseDecision['decision']) => {
    if (settled) {
      return;
    }
    stop();
    onSettled(decision, InboxModule.releaseSelection(holdToken));
  };

  pending.set(registration.token, { abort: () => finish('abort') });
  try {
    dispose = requestLeave(registration.bus, {
      onProceed: () => finish('proceed'),
      onAbort: () => finish('abort'),
    });
  } catch (error) {
    // never leave a hold behind with no handshake to release it
    stop();
    InboxModule.releaseSelection(holdToken);
    throw error;
  }
  return () => {
    if (settled) {
      return;
    }
    stop();
    InboxModule.releaseSelection(holdToken);
  };
}

/**
 * Asks one view's customer-mfe whether a route change may discard its
 * booking. Holds selection on the view (the home view holds on `undefined`)
 * until the MFE answers, so an automatic selection change cannot hide the
 * prompt; the deferred request is applied after either decision. A second
 * blocked navigation disposes this request and asks again, dropping any
 * selection deferred before it so the prompt stays visible. Unlike
 * `requestClose` it does not select the task and never confirms a Close.
 * Proceeds at once when the view has no registration; aborts if the view
 * unregisters before it answers.
 *
 * @param viewKey - the registered view to ask
 * @param handlers - called once with the MFE's decision
 * @returns a dispose function that drops the request and releases the hold
 *   without calling either handler or applying the deferred request
 */
export function requestLeaveForView(
  viewKey: string,
  { onProceed, onAbort }: LeaveDecisionHandlers,
): () => void {
  const registration = registrations.get(viewKey);
  if (!registration) {
    onProceed();
    return () => {};
  }
  const holdToken = InboxModule.holdSelection(
    viewKey === HOME_VIEW_KEY
      ? undefined
      : TasksModule.getTaskByViewKey(viewKey)?.id,
  );
  return askToLeave(
    registration,
    pendingLeaveByToken,
    holdToken,
    (decision, deferredSelection) => {
      const handle = decision === 'proceed' ? onProceed : onAbort;
      handle();
      InboxModule.applyDeferredSelection(deferredSelection);
    },
  );
}

/**
 * Re-sends `request-leave` so customer-mfe joins its already-open discard
 * modal. Does nothing unless a Close handshake is pending (the MFE would
 * otherwise open a fresh modal).
 */
export function rejoinClosePrompt(viewKey: string): void {
  if (isHandshakePending(viewKey)) {
    registrations.get(viewKey)?.bus.emit(REQUEST_LEAVE_EVENT);
  }
}

/**
 * Asks the CSR whether closing the card may discard its booking: through the
 * card's own customer-mfe prompt when the CSR was looking at the card
 * (`askMfe`), else with the host's prompt, which names the card. Proceeds
 * without a prompt when there is no registration, no active task, or no
 * booking in progress any more (the CSR may have closed it while Close was
 * checking).
 *
 * Selects the task as a CSR click only when it is not already selected, holds
 * selection while the prompt is open so a call offered meanwhile cannot move
 * it, and releases the hold on every exit, returning the deferred request
 * instead of applying it. While another Close is asking it aborts at once and
 * leaves that prompt and its selection hold alone; the view unregistering
 * aborts its own.
 */
export async function requestClose(
  viewKey: string,
  { askMfe = false }: { askMfe?: boolean } = {},
): Promise<CloseDecision> {
  const registration = registrations.get(viewKey);
  const task = TasksModule.getTaskByViewKey(viewKey);
  if (!registration || !task) {
    return { decision: 'proceed' };
  }
  if (!registration.isBookingGuarded()) {
    // nothing left to discard: confirmed as if asked, so the view still
    // latches against a rebuild while the card closes
    registration.onCloseConfirmed?.();
    return { decision: 'proceed' };
  }
  // another prompt is asking (another card's Close, or leaving the Inbox, which
  // a nav click can start while this Close was checking): never stack a second
  if (isCloseAsking() || isLeaveAsking()) {
    return { decision: 'abort' };
  }

  if (inboxState.selectedTaskId !== task.id) {
    InboxModule.selectTaskByUser(task.id);
  }

  const settle = (
    decision: CloseDecision['decision'],
    deferredSelection: DeferredSelection | undefined,
  ): CloseDecision => {
    if (decision === 'proceed') {
      registration.onCloseConfirmed?.();
    }
    return { decision, ...(deferredSelection ? { deferredSelection } : {}) };
  };

  if (askMfe) {
    try {
      return await askMfeToClose(
        registration,
        InboxModule.holdSelection(task.id),
        settle,
      );
    } catch (error) {
      // the hold is already released; ask with the host prompt instead, as
      // leaving the Inbox does, so a throwing MFE cannot leave the card
      // unclosable
      logger.error('Close handshake failed', error);
      setCloseState(viewKey, 'prompt');
    }
  }

  return askHostToClose(
    registration,
    viewKey,
    InboxModule.holdSelection(task.id),
    settle,
  );
}

type Settle = (
  decision: CloseDecision['decision'],
  deferredSelection: DeferredSelection | undefined,
) => CloseDecision;

/** A Close asked through the card's customer-mfe prompt; rejects if the MFE throws. */
function askMfeToClose(
  registration: Registration,
  holdToken: number,
  settle: Settle,
): Promise<CloseDecision> {
  return new Promise<CloseDecision>((resolve, reject) => {
    handshakeTokens.add(registration.token);
    try {
      askToLeave(
        registration,
        pendingByToken,
        holdToken,
        (decision, deferredSelection) => {
          handshakeTokens.delete(registration.token);
          resolve(settle(decision, deferredSelection));
        },
      );
    } catch (error) {
      handshakeTokens.delete(registration.token);
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

/** A Close asked through the host's prompt, which names the card. */
function askHostToClose(
  registration: Registration,
  viewKey: string,
  holdToken: number,
  settle: Settle,
): Promise<CloseDecision> {
  return new Promise<CloseDecision>((resolve) => {
    let settled = false;
    const decide = (decision: CloseDecision['decision']) => {
      if (settled) {
        return;
      }
      settled = true;
      pendingByToken.delete(registration.token);
      if (closePrompt === prompt) {
        setClosePrompt(undefined);
      }
      resolve(settle(decision, InboxModule.releaseSelection(holdToken)));
    };
    const prompt: ClosePrompt = { viewKey, decide };

    pendingByToken.set(registration.token, { abort: () => decide('abort') });
    setClosePrompt(prompt);
  });
}

/** `handshake`: customer-mfe's own prompt is open; `prompt`: the host's is. */
export type CloseState = 'checking' | 'handshake' | 'prompt';

// Close state lives here, not in React state, so InboxPage's view cap can read
// it, and independent of registrations so cards with no registered guard are
// de-duplicated too. Keyed by viewKey, falling back to the task id.
const closeStates = new Map<string, CloseState>();

export function getCloseState(key: string): CloseState | 'idle' {
  return closeStates.get(key) ?? 'idle';
}

export function setCloseState(key: string, state: CloseState | 'idle'): void {
  if (state === 'idle') {
    closeStates.delete(key);
  } else {
    closeStates.set(key, state);
  }
}
